import { Button } from "@dmca/frontend/components/button";
import {
  NotificationAdminModal,
  type NotificationAdminModalSubmitValue,
} from "@dmca/frontend/widgets/admin/notification";
import type { IUser } from "@dmca/models/interface/user";
import type { ContactInfo } from "@dmca/utils/types";
import { parsePhoneNumberWithError } from "@dmca/utils/libphonenumber";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "react-toastify";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/admin/notification/kanban-order-approval"
)({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc } }) => {
    const [contacts, users] = await Promise.all([
      qc.ensureQueryData(trpc.kanban.getOrderApprovalContact.queryOptions()),
      qc.ensureQueryData(trpc.user.getAll.queryOptions()),
    ]);

    return { contacts, users };
  },
});

function formatContact(contact: ContactInfo, users: IUser[]) {
  if (contact.inputType === "user") {
    const user = users.find((item) => item.id === contact.value);
    return user ? `${user.name} - SMS` : "Unknown user - SMS";
  }

  return parsePhoneNumberWithError(contact.value).formatNational();
}

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const { contacts: initialContacts, users: initialUsers } =
    Route.useLoaderData();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: contacts } = useQuery(
    trpc.kanban.getOrderApprovalContact.queryOptions(undefined, {
      initialData: initialContacts,
    })
  );
  const { data: users } = useQuery(
    trpc.user.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
      initialData: initialUsers,
    })
  );

  const resetPage = () => {
    setModalOpen(false);
    qc.invalidateQueries(trpc.kanban.getOrderApprovalContact.queryFilter());
  };

  const upsertMutation = useMutation(
    trpc.kanban.upsertOrderApprovalContact.mutationOptions({
      onSuccess: () => {
        toast.success("Kanban approval phone number updated.");
        resetPage();
      },
      onError: () => {
        toast.error("Failed to update Kanban approval phone number.");
      },
    })
  );

  const submitContact = (value: NotificationAdminModalSubmitValue) => {
    const nextContacts = [
      ...(contacts ?? [])
        .filter(
          (contact) => !value.delete.some((item) => item.id === contact.id)
        )
        .map(
          (contact) =>
            value.update.find((item) => item.id === contact.id)?.contact ??
            contact.contact
        ),
      ...value.add.map((item) => item.contact),
    ];
    upsertMutation.mutate({ contacts: nextContacts });
  };

  return (
    <>
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Typography>
          Configure the SMS contacts that approve Kanban order creation and
          completion from the dashboard
        </Typography>
        <Stack spacing={1}>
          <Typography variant="h6">Approval Phone Numbers</Typography>
          {contacts?.length ? (
            contacts.map((contact) => (
              <Typography key={contact.id}>
                {formatContact(contact.contact, users ?? [])}
              </Typography>
            ))
          ) : (
            <Typography>No approval phone numbers configured.</Typography>
          )}
        </Stack>
        <Button
          onClick={() => setModalOpen(true)}
          sx={{ width: "fit-content" }}
        >
          {contacts?.length ? "Update Approval Phones" : "Add Approval Phone"}
        </Button>
      </Stack>
      {modalOpen && (
        <NotificationAdminModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          header="Kanban Order Approval Phone"
          availableContactTypes={["sms"]}
          modalType={contacts?.length ? "update" : "add"}
          notifications={contacts ?? []}
          users={users ?? []}
          onSubmit={submitContact}
          isSubmitting={upsertMutation.isPending}
        />
      )}
    </>
  );
}
